import { useState } from "react";
import { Link } from "react-router-dom";
import { useNavigate } from "@/lib/useNav";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Sparkles, Lock, Mail, ShieldCheck, Loader2, Fingerprint } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PasswordInput } from "@/components/form/PasswordInput";
import { OFFER_KEY } from "@/components/shared/PasskeyPrompt";

export default function Login() {
  const { login, loginMfa, loginPasskey } = useAuth();
  const [pkBusy, setPkBusy] = useState(false);
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [challenge, setChallenge] = useState(null);
  const [code, setCode] = useState("");
  const [remember, setRemember] = useState(true);

  const friendlyError = (e, fallback) => {
    const status = e.response?.status;
    const data = e.response?.data;
    // No response (network), a 5xx, or a non-JSON (HTML/Cloudflare) body => transient restart/deploy
    const transient = !e.response || (status && status >= 500) || typeof data === "string";
    if (transient) return "DreamCRM is applying a quick system update and reloading. Please wait a minute or two, then try signing in again.";
    return formatApiErrorDetail(data?.detail) || fallback;
  };

  const passkey = async () => {
    setPkBusy(true);
    setError("");
    try {
      await loginPasskey();
      navigate("/dashboard");
    } catch (e) {
      if (e?.name === "NotAllowedError" || e?.name === "AbortError") setError("Passkey sign-in was cancelled. First time? Sign in with your password, then add a passkey in Settings → My Settings.");
      else if (e?.response) setError(friendlyError(e, "Passkey sign-in failed"));
      else setError("Passkey sign-in isn't available in this browser. Use your email and password instead.");
    } finally {
      setPkBusy(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);
    try {
      const res = await login(email, password);
      if (res.mfaRequired) {
        setChallenge(res.challenge);
      } else {
        sessionStorage.setItem(OFFER_KEY, "1");
        navigate("/dashboard");
      }
    } catch (e) {
      setError(friendlyError(e, "Unable to sign in"));
    } finally {
      setLoading(false);
    }
  };

  const submitMfa = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await loginMfa(challenge, code, remember);
      sessionStorage.setItem(OFFER_KEY, "1");
      navigate("/dashboard");
    } catch (e) {
      setError(friendlyError(e, "Invalid code"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen">
      {/* Left brand panel */}
      <div className="relative hidden w-1/2 flex-col justify-between overflow-hidden bg-slate-900 p-12 lg:flex">
        <div
          className="absolute inset-0 opacity-40"
          style={{
            backgroundImage:
              "url(https://images.unsplash.com/photo-1582719508461-905c673771fd?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200)",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900/90 via-slate-900/70 to-sky-900/60" />
        <div className="relative flex items-center gap-2.5">
          <Sparkles className="h-8 w-8 text-white" />
          <div>
            <p className="font-heading text-lg font-extrabold text-white">DreamCRM</p>
            <p className="text-xs text-sky-200">Dreamtime Resorts</p>
          </div>
        </div>
        <div className="relative max-w-md">
          <h2 className="font-heading text-3xl font-bold leading-tight text-white">
            Success happens one interaction at a time.
          </h2>
          <p className="mt-4 text-sm leading-relaxed text-slate-300">
            Track the progress. Nurture the relationship. Grow the business.
          </p>
        </div>
        <div className="relative flex items-center gap-2 text-xs text-slate-400">
          <ShieldCheck className="h-4 w-4 text-emerald-400" /> 256-bit encrypted internal system
        </div>
      </div>

      {/* Right form */}
      <div className="flex w-full items-center justify-center bg-white px-6 lg:w-1/2">
        <div className="w-full max-w-sm animate-fade-up">
          <div className="mb-8 flex items-center gap-2.5 lg:hidden">
            <Sparkles className="h-8 w-8 text-sky-600" />
            <div>
              <p className="font-heading text-xl font-extrabold text-slate-900">DreamCRM</p>
              <p className="text-xs text-slate-400">Dreamtime Resorts</p>
            </div>
          </div>
          <h1 className="font-heading text-2xl font-bold text-slate-900">Welcome back</h1>
          <p className="mt-1 text-sm text-slate-500">Sign in to your DreamCRM workspace.</p>

          {challenge ? (
            <form onSubmit={submitMfa} className="mt-8 space-y-4" data-testid="mfa-form">
              <p className="text-sm text-slate-600">Enter the 6-digit code from your authenticator app, or a backup code.</p>
              <div>
                <label className="mb-1.5 block text-xs font-semibold text-slate-600">Authentication code</label>
                <input
                  data-testid="mfa-code"
                  inputMode="text"
                  autoFocus
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  placeholder="123456"
                  className="w-full rounded-lg border border-slate-200 py-2.5 px-3 text-sm tracking-widest focus:border-sky-400 focus:outline-none focus:ring-2 focus:ring-sky-100"
                />
              </div>
              <label className="flex items-center gap-2 text-sm text-slate-600">
                <input type="checkbox" data-testid="mfa-remember" checked={remember} onChange={(e) => setRemember(e.target.checked)}
                  className="h-4 w-4 rounded border-slate-300 text-sky-600 focus:ring-sky-500" />
                Remember this device for 30 days
              </label>
              {error && <p data-testid="login-error" className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-600">{error}</p>}
              <Button type="submit" disabled={loading} data-testid="mfa-submit" className="w-full bg-sky-600 py-2.5 text-sm font-semibold hover:bg-sky-700">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Verify & sign in"}
              </Button>
              <button type="button" onClick={() => { setChallenge(null); setCode(""); setError(""); }} className="w-full text-center text-xs font-medium text-slate-400 hover:text-slate-600">Back to sign in</button>
            </form>
          ) : (
          <form onSubmit={submit} className="mt-8 space-y-4">
            <div>
              <label className="mb-1.5 block text-xs font-semibold text-slate-600">Email</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <input
                  data-testid="login-email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@dreamtimeresorts.com.au"
                  className="w-full rounded-lg border border-slate-200 py-2.5 pl-9 pr-3 text-sm focus:border-sky-400 focus:outline-none focus:ring-2 focus:ring-sky-100"
                />
              </div>
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-semibold text-slate-600">Password</label>
              <div className="relative">
                <Lock className="pointer-events-none absolute left-3 top-1/2 z-10 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <PasswordInput
                  data-testid="login-password"
                  
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="w-full rounded-lg border border-slate-200 py-2.5 pl-9 pr-3 text-sm focus:border-sky-400 focus:outline-none focus:ring-2 focus:ring-sky-100"
                />
              </div>
            </div>

            {error && (
              <p data-testid="login-error" className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-600">{error}</p>
            )}

            <Button
              type="submit"
              disabled={loading}
              data-testid="login-submit"
              className="w-full bg-sky-600 py-2.5 text-sm font-semibold hover:bg-sky-700 active:scale-[0.99]"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign in"}
            </Button>

            <div className="relative py-1 text-center text-[11px] font-medium uppercase tracking-wider text-slate-300">
              <span className="relative z-10 bg-white px-2">or</span>
              <span className="absolute inset-x-0 top-1/2 h-px bg-slate-100" />
            </div>
            <Button type="button" variant="outline" onClick={passkey} disabled={pkBusy} data-testid="login-passkey"
              className="w-full gap-2 py-2.5 text-sm font-semibold text-slate-700">
              {pkBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Fingerprint className="h-4 w-4 text-sky-600" />}
              Sign in with passkey
            </Button>
            <p className="-mt-1 text-center text-[11px] text-slate-400">Face ID, Touch ID, Windows Hello or scan a QR code with your phone</p>

            <div className="text-center">
              <Link to="/forgot-password" data-testid="forgot-password-link" className="text-xs font-medium text-sky-600 hover:text-sky-800">Forgot password?</Link>
            </div>
          </form>
          )}
          <p className="mt-6 text-center text-xs text-slate-400">Internal system · Access is managed by your administrator</p>
        </div>
      </div>
    </div>
  );
}
